import {
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CustomerModal from '../../components/customers/CustomerModal';
import { apiService } from '../../services/api';

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  status: string;
  assigned_salesperson_name?: string;
  created_at: string;
  interested_vehicles?: Array<{
    id: string;
    year: number;
    make: string;
    model: string;
    asking_price: number;
    priority_order: number;
    notes?: string;
    primary_image?: string;
  }>;
  deal_status?: 'INQUIRY' | 'LEAD' | 'PROSPECT' | 'NEGOTIATION' | 'SALE' | 'LOST' | null;
}

interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  order: number;
}

interface TableStyleConfig {
  // Row styling
  rowHeight: string;
  rowPadding: string;
  rowTextAlign: string;

  // Typography
  fontSize: string;
  fontFamily: string;
  fontWeight: string;
  lineHeight: string;

  // Colors
  rowBgColor: string;
  rowTextColor: string;
  rowHoverColor: string;
  rowHoverTextColor: string;
  alternatingRowBg: boolean;
  alternatingRowBgColor: string;

  // Borders
  borderWidth: string;
  borderColor: string;
  borderStyle: string;
  borderRadius: string;

  // Header styling
  headerBgColor: string;
  headerTextColor: string;
  headerFontWeight: string;
  headerFontSize: string;
  headerPadding: string;
  headerTextAlign: string;
  headerBorderColor: string;

  // Cell styling
  cellPadding: string;
  cellBorderColor: string;
  cellVerticalAlign: string;
}

const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Table styling modal state
  const [tableStylingOpen, setTableStylingOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'styling' | 'columns'>('styling');
  const [activeKPI, setActiveKPI] = useState<'overview' | 'rows' | 'header' | 'colors'>('overview');

  // Column configuration
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([
    { key: 'name', label: 'Name', visible: true, order: 0 },
    { key: 'contact', label: 'Contact', visible: true, order: 1 },
    { key: 'status', label: 'Status', visible: true, order: 2 },
    { key: 'interested_vehicle', label: 'Interested Vehicle', visible: true, order: 3 },
    { key: 'deal_status', label: 'Deal Status', visible: true, order: 4 },
    { key: 'salesperson', label: 'Salesperson', visible: true, order: 5 },
    { key: 'created', label: 'Created', visible: true, order: 6 },
    { key: 'actions', label: 'Actions', visible: true, order: 7 },
  ]);

  // Table styling configuration
  const [tableStyle, setTableStyle] = useState<TableStyleConfig>({
    // Row styling
    rowHeight: '3rem',
    rowPadding: '0.75rem',
    rowTextAlign: 'left',

    // Typography
    fontSize: '0.875rem',
    fontFamily: 'system-ui',
    fontWeight: '400',
    lineHeight: '1.5',

    // Colors
    rowBgColor: '#ffffff',
    rowTextColor: '#374151',
    rowHoverColor: '#f9fafb',
    rowHoverTextColor: '#374151',
    alternatingRowBg: false,
    alternatingRowBgColor: '#f8fafc',

    // Borders
    borderWidth: '1px',
    borderColor: '#e5e7eb',
    borderStyle: 'solid',
    borderRadius: '0.375rem',

    // Header styling
    headerBgColor: '#f9fafb',
    headerTextColor: '#374151',
    headerFontWeight: '600',
    headerFontSize: '0.875rem',
    headerPadding: '0.75rem',
    headerTextAlign: 'left',
    headerBorderColor: '#e5e7eb',

    // Cell styling
    cellPadding: '0.75rem',
    cellBorderColor: '#e5e7eb',
    cellVerticalAlign: 'middle',
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<Customer[]>('/customers/');
      // Ensure response is an array
      const customersList = Array.isArray(response) ? response : [];
      setCustomers(customersList);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setModalOpen(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setModalOpen(true);
  };

  const handleDeleteCustomer = (customer: Customer) => {
    setCustomerToDelete(customer);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;

    try {
      setActionLoading(true);
      await apiService.delete(`/customers/${customerToDelete.id}`);
      setCustomers((prev) => prev.filter((c) => c.id !== customerToDelete.id));
      setDeleteConfirmOpen(false);
      setCustomerToDelete(null);
    } catch (error) {
      console.error('Error deleting customer:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveCustomer = async (customerData: Partial<Customer>) => {
    try {
      setActionLoading(true);
      let savedCustomer: Customer;
      if (selectedCustomer) {
        // Update existing customer
        savedCustomer = await apiService.put<Customer>(
          `/customers/${selectedCustomer.id}`,
          customerData
        );
        setCustomers((prev) => prev.map((c) => (c.id === selectedCustomer.id ? savedCustomer : c)));
      } else {
        // Create new customer
        savedCustomer = await apiService.post<Customer>('/customers/', customerData);
        setCustomers((prev) => [savedCustomer, ...prev]);
      }
      setModalOpen(false);
      setSelectedCustomer(null);
      return savedCustomer; // Return the saved customer for interested vehicles functionality
    } catch (error) {
      console.error('Error saving customer:', error);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      !searchTerm ||
      customer.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.phone && customer.phone.includes(searchTerm)) ||
      (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = !statusFilter || customer.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      INACTIVE: 'bg-gray-100 text-gray-800',
      DO_NOT_CALL: 'bg-red-100 text-red-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getDealStatusBadge = (dealStatus?: string | null) => {
    if (!dealStatus) return 'bg-gray-100 text-gray-500';

    const colors = {
      INQUIRY: 'bg-blue-100 text-blue-800',
      LEAD: 'bg-purple-100 text-purple-800',
      PROSPECT: 'bg-yellow-100 text-yellow-800',
      NEGOTIATION: 'bg-orange-100 text-orange-800',
      SALE: 'bg-green-100 text-green-800',
      LOST: 'bg-red-100 text-red-800',
    };
    return colors[dealStatus as keyof typeof colors] || 'bg-gray-100 text-gray-500';
  };

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your dealership customers and their information.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex gap-2">
          <button className="btn-secondary" onClick={() => setTableStylingOpen(true)}>
            <Cog6ToothIcon className="h-4 w-4 mr-2" />
            Customize Table
          </button>
          <button className="btn-primary" onClick={handleAddCustomer}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Search and filters */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="form-input pl-10"
                placeholder="Search customers by name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              className="form-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="DO_NOT_CALL">Do Not Call</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customer list */}
      <div className="card">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading customers...</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PlusIcon className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
            <p className="text-gray-500 mb-6">
              {customers.length === 0
                ? 'Get started by adding your first customer to the system.'
                : 'Try adjusting your search or filters.'}
            </p>
            {customers.length === 0 && (
              <button className="btn-primary" onClick={handleAddCustomer}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Your First Customer
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table
              className="min-w-full"
              style={{
                fontSize: tableStyle.fontSize,
                fontFamily: tableStyle.fontFamily,
                fontWeight: tableStyle.fontWeight,
                lineHeight: tableStyle.lineHeight,
                borderCollapse: 'separate',
                borderSpacing: '0',
                borderRadius: tableStyle.borderRadius,
                border: `${tableStyle.borderWidth} ${tableStyle.borderStyle} ${tableStyle.borderColor}`,
                overflow: 'hidden',
              }}
            >
              <thead
                style={{
                  backgroundColor: tableStyle.headerBgColor,
                  color: tableStyle.headerTextColor,
                  fontWeight: tableStyle.headerFontWeight,
                  fontSize: tableStyle.headerFontSize,
                }}
              >
                <tr>
                  {columnConfig
                    .filter((col) => col.visible)
                    .sort((a, b) => a.order - b.order)
                    .map((col) => (
                      <th
                        key={col.key}
                        style={{
                          padding: tableStyle.headerPadding,
                          textAlign: tableStyle.headerTextAlign as any,
                          borderBottom: `${tableStyle.borderWidth} ${tableStyle.borderStyle} ${tableStyle.headerBorderColor}`,
                          borderRight:
                            col.key !== 'actions'
                              ? `${tableStyle.borderWidth} ${tableStyle.borderStyle} ${tableStyle.headerBorderColor}`
                              : 'none',
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer, index) => (
                  <tr
                    key={customer.id}
                    className="transition-colors duration-150"
                    style={{
                      height: tableStyle.rowHeight,
                      backgroundColor:
                        tableStyle.alternatingRowBg && index % 2 === 1
                          ? tableStyle.alternatingRowBgColor
                          : tableStyle.rowBgColor,
                      color: tableStyle.rowTextColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = tableStyle.rowHoverColor;
                      e.currentTarget.style.color = tableStyle.rowHoverTextColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        tableStyle.alternatingRowBg && index % 2 === 1
                          ? tableStyle.alternatingRowBgColor
                          : tableStyle.rowBgColor;
                      e.currentTarget.style.color = tableStyle.rowTextColor;
                    }}
                  >
                    {columnConfig
                      .filter((col) => col.visible)
                      .sort((a, b) => a.order - b.order)
                      .map((col) => (
                        <td
                          key={col.key}
                          style={{
                            padding: tableStyle.cellPadding,
                            textAlign: tableStyle.rowTextAlign as any,
                            verticalAlign: tableStyle.cellVerticalAlign as any,
                            borderRight:
                              col.key !== 'actions'
                                ? `${tableStyle.borderWidth} ${tableStyle.borderStyle} ${tableStyle.cellBorderColor}`
                                : 'none',
                          }}
                        >
                          {col.key === 'name' && (
                            <div className="font-medium text-gray-900">
                              {customer.first_name} {customer.last_name}
                            </div>
                          )}

                          {col.key === 'contact' && (
                            <div className="text-sm">
                              <div>{customer.phone}</div>
                              <div className="text-gray-500">{customer.email}</div>
                            </div>
                          )}

                          {col.key === 'status' && (
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(customer.status)}`}
                            >
                              {customer.status.replace('_', ' ')}
                            </span>
                          )}

                          {col.key === 'interested_vehicle' && (
                            <div className="space-y-2">
                              {customer.interested_vehicles?.map((vehicle) => (
                                <div key={vehicle.id} className="flex items-center space-x-2">
                                  <div className="w-10 h-6 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                                    {vehicle.primary_image ? (
                                      <img
                                        src={apiService.getFileUrl(vehicle.primary_image)}
                                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          const img = e.target as HTMLImageElement;
                                          img.style.display = 'none';
                                          img.parentElement!.innerHTML =
                                            '<span class="text-xs text-gray-400 flex items-center justify-center h-full">No Image</span>';
                                        }}
                                      />
                                    ) : (
                                      <span className="text-xs text-gray-400 flex items-center justify-center h-full">
                                        No Image
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium text-gray-900 truncate">
                                      {vehicle.year} {vehicle.make}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                      {vehicle.model}
                                    </div>
                                    {vehicle.asking_price && (
                                      <div className="text-xs text-green-600 font-medium">
                                        ${vehicle.asking_price.toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                  {vehicle.priority_order && (
                                    <div className="flex-shrink-0">
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        #{vehicle.priority_order}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {(!customer.interested_vehicles ||
                                customer.interested_vehicles.length === 0) && (
                                <span className="text-gray-400 text-xs">No vehicles</span>
                              )}
                            </div>
                          )}

                          {col.key === 'deal_status' && (
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDealStatusBadge(customer.deal_status)}`}
                            >
                              {customer.deal_status || 'None'}
                            </span>
                          )}

                          {col.key === 'salesperson' && (
                            <div className="text-gray-500">
                              {customer.assigned_salesperson_name || 'Unassigned'}
                            </div>
                          )}

                          {col.key === 'created' && (
                            <div className="text-gray-500">{formatDate(customer.created_at)}</div>
                          )}

                          {col.key === 'actions' && (
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/customers/${customer.id}`}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                View
                              </Link>
                              <Link
                                to={`/customers/${customer.id}/workspace`}
                                className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                              >
                                Workspace
                              </Link>
                              <button
                                className="text-gray-600 hover:text-blue-600 text-sm font-medium flex items-center"
                                onClick={() => handleEditCustomer(customer)}
                              >
                                <PencilIcon className="h-4 w-4 mr-1" />
                                Edit
                              </button>
                              <button
                                className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center"
                                onClick={() => handleDeleteCustomer(customer)}
                              >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customer Modal */}
      <CustomerModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedCustomer(null);
        }}
        onSave={handleSaveCustomer}
        customer={selectedCustomer}
        loading={actionLoading}
      />

      {/* Table Styling Modal */}
      {tableStylingOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setTableStylingOpen(false)}
            />

            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6">
              <div className="mb-6 relative">
                <h3 className="text-lg font-semibold leading-6 text-gray-900 flex items-center pr-8">
                  <Cog6ToothIcon className="h-6 w-6 mr-2" />
                  Customize Table
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Change how your customer list looks and what information shows
                </p>
                <button
                  onClick={() => setTableStylingOpen(false)}
                  className="absolute top-0 right-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'styling'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab('styling')}
                  >
                    Appearance
                  </button>
                  <button
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'columns'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab('columns')}
                  >
                    Information Shown
                  </button>
                </nav>
              </div>

              {/* Styling Tab */}
              {activeTab === 'styling' && (
                <div>
                  {/* KPI Overview - Default View */}
                  {activeKPI === 'overview' && (
                    <div className="space-y-4">
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-600 mb-2">
                          Choose what to customize:
                        </h4>
                      </div>

                      {/* Table Rows KPI Card */}
                      <div
                        className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all group"
                        onClick={() => setActiveKPI('rows')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full mr-3"></div>
                            <div>
                              <h4 className="font-semibold text-blue-900 text-lg">Customer Rows</h4>
                              <p className="text-sm text-blue-700 mt-1">
                                Row height, spacing, text size, and positioning
                              </p>
                              <div className="flex items-center mt-2 space-x-2">
                                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                  6 settings
                                </span>
                                <span className="text-xs text-blue-500">
                                  Current: {tableStyle.rowHeight} height, {tableStyle.fontFamily}{' '}
                                  font
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-blue-400 group-hover:text-blue-600 transition-colors">
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Table Header KPI Card */}
                      <div
                        className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-lg p-4 cursor-pointer hover:border-emerald-300 hover:shadow-md transition-all group"
                        onClick={() => setActiveKPI('header')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full mr-3"></div>
                            <div>
                              <h4 className="font-semibold text-emerald-900 text-lg">
                                Column Headers
                              </h4>
                              <p className="text-sm text-emerald-700 mt-1">
                                Header text size, positioning, spacing, and style
                              </p>
                              <div className="flex items-center mt-2 space-x-2">
                                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                                  5 settings
                                </span>
                                <span className="text-xs text-emerald-500">
                                  Current: {tableStyle.headerFontWeight},{' '}
                                  {tableStyle.headerTextAlign}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-emerald-400 group-hover:text-emerald-600 transition-colors">
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Colors & Borders KPI Card */}
                      <div
                        className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg p-4 cursor-pointer hover:border-amber-300 hover:shadow-md transition-all group"
                        onClick={() => setActiveKPI('colors')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full mr-3"></div>
                            <div>
                              <h4 className="font-semibold text-amber-900 text-lg">
                                Colors & Lines
                              </h4>
                              <p className="text-sm text-amber-700 mt-1">
                                Background colors, text colors, and border lines
                              </p>
                              <div className="flex items-center mt-2 space-x-2">
                                <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                                  8 settings
                                </span>
                                <span className="text-xs text-amber-500">
                                  Current: {tableStyle.rowBgColor}, {tableStyle.borderStyle}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-amber-400 group-hover:text-amber-600 transition-colors">
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Table Rows Setup */}
                  {activeKPI === 'rows' && (
                    <div className="space-y-6">
                      <div className="flex items-center mb-4">
                        <button
                          onClick={() => setActiveKPI('overview')}
                          className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                          Back to Overview
                        </button>
                      </div>

                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                        <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center">
                          <div className="w-5 h-5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full mr-2"></div>
                          Customer Row Settings
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <h4 className="font-medium text-blue-800">Row Size</h4>
                            <div>
                              <label className="form-label text-blue-800">Row Height</label>
                              <select
                                className="form-input"
                                value={tableStyle.rowHeight}
                                onChange={(e) =>
                                  setTableStyle((prev) => ({ ...prev, rowHeight: e.target.value }))
                                }
                              >
                                <option value="2rem">Extra Compact (32px)</option>
                                <option value="2.5rem">Compact (40px)</option>
                                <option value="3rem">Default (48px)</option>
                                <option value="3.5rem">Comfortable (56px)</option>
                                <option value="4rem">Spacious (64px)</option>
                              </select>
                            </div>
                            <div>
                              <label className="form-label text-blue-800">Text Spacing</label>
                              <select
                                className="form-input"
                                value={tableStyle.cellPadding}
                                onChange={(e) =>
                                  setTableStyle((prev) => ({
                                    ...prev,
                                    cellPadding: e.target.value,
                                    rowPadding: e.target.value,
                                  }))
                                }
                              >
                                <option value="0.5rem">Small (8px)</option>
                                <option value="0.75rem">Default (12px)</option>
                                <option value="1rem">Large (16px)</option>
                                <option value="1.25rem">Extra Large (20px)</option>
                              </select>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <h4 className="font-medium text-blue-800">Text Style</h4>
                            <div>
                              <label className="form-label text-blue-800">Font Family</label>
                              <select
                                className="form-input"
                                value={tableStyle.fontFamily}
                                onChange={(e) =>
                                  setTableStyle((prev) => ({ ...prev, fontFamily: e.target.value }))
                                }
                              >
                                <option value="system-ui">System UI</option>
                                <option value="Inter">Inter</option>
                                <option value="Arial">Arial</option>
                                <option value="Georgia">Georgia</option>
                                <option value="Helvetica">Helvetica</option>
                                <option value="monospace">Monospace</option>
                              </select>
                            </div>
                            <div>
                              <label className="form-label text-blue-800">Font Size</label>
                              <select
                                className="form-input"
                                value={tableStyle.fontSize}
                                onChange={(e) =>
                                  setTableStyle((prev) => ({ ...prev, fontSize: e.target.value }))
                                }
                              >
                                <option value="0.625rem">Extra Small (10px)</option>
                                <option value="0.75rem">Small (12px)</option>
                                <option value="0.875rem">Default (14px)</option>
                                <option value="1rem">Large (16px)</option>
                                <option value="1.125rem">Extra Large (18px)</option>
                              </select>
                            </div>
                            <div>
                              <label className="form-label text-blue-800">Text Alignment</label>
                              <select
                                className="form-input"
                                value={tableStyle.rowTextAlign}
                                onChange={(e) =>
                                  setTableStyle((prev) => ({
                                    ...prev,
                                    rowTextAlign: e.target.value,
                                  }))
                                }
                              >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Table Header Setup */}
                  {activeKPI === 'header' && (
                    <div className="space-y-6">
                      <div className="flex items-center mb-4">
                        <button
                          onClick={() => setActiveKPI('overview')}
                          className="flex items-center text-emerald-600 hover:text-emerald-800 text-sm font-medium"
                        >
                          <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                          Back to Overview
                        </button>
                      </div>

                      <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-6">
                        <h3 className="text-xl font-bold text-emerald-900 mb-4 flex items-center">
                          <div className="w-5 h-5 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full mr-2"></div>
                          Column Header Settings
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <h4 className="font-medium text-emerald-800">Text Style</h4>
                            <div>
                              <label className="form-label text-emerald-800">Font Size</label>
                              <select
                                className="form-input"
                                value={tableStyle.headerFontSize}
                                onChange={(e) =>
                                  setTableStyle((prev) => ({
                                    ...prev,
                                    headerFontSize: e.target.value,
                                  }))
                                }
                              >
                                <option value="0.625rem">Extra Small (10px)</option>
                                <option value="0.75rem">Small (12px)</option>
                                <option value="0.875rem">Default (14px)</option>
                                <option value="1rem">Large (16px)</option>
                                <option value="1.125rem">Extra Large (18px)</option>
                              </select>
                            </div>
                            <div>
                              <label className="form-label text-emerald-800">Font Weight</label>
                              <select
                                className="form-input"
                                value={tableStyle.headerFontWeight}
                                onChange={(e) =>
                                  setTableStyle((prev) => ({
                                    ...prev,
                                    headerFontWeight: e.target.value,
                                  }))
                                }
                              >
                                <option value="400">Normal</option>
                                <option value="500">Medium</option>
                                <option value="600">Semi Bold</option>
                                <option value="700">Bold</option>
                                <option value="800">Extra Bold</option>
                              </select>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <h4 className="font-medium text-emerald-800">Layout</h4>
                            <div>
                              <label className="form-label text-emerald-800">Text Alignment</label>
                              <select
                                className="form-input"
                                value={tableStyle.headerTextAlign}
                                onChange={(e) =>
                                  setTableStyle((prev) => ({
                                    ...prev,
                                    headerTextAlign: e.target.value,
                                  }))
                                }
                              >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                              </select>
                            </div>
                            <div>
                              <label className="form-label text-emerald-800">Padding</label>
                              <select
                                className="form-input"
                                value={tableStyle.headerPadding}
                                onChange={(e) =>
                                  setTableStyle((prev) => ({
                                    ...prev,
                                    headerPadding: e.target.value,
                                  }))
                                }
                              >
                                <option value="0.5rem">Small (8px)</option>
                                <option value="0.75rem">Default (12px)</option>
                                <option value="1rem">Large (16px)</option>
                                <option value="1.25rem">Extra Large (20px)</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Colors & Borders Setup */}
                  {activeKPI === 'colors' && (
                    <div className="space-y-6">
                      <div className="flex items-center mb-4">
                        <button
                          onClick={() => setActiveKPI('overview')}
                          className="flex items-center text-amber-600 hover:text-amber-800 text-sm font-medium"
                        >
                          <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                          Back to Overview
                        </button>
                      </div>

                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
                        <h3 className="text-xl font-bold text-amber-900 mb-4 flex items-center">
                          <div className="w-5 h-5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full mr-2"></div>
                          Colors & Lines Settings
                        </h3>

                        <div className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <h4 className="font-medium text-amber-800">Customer Row Colors</h4>
                              <div>
                                <label className="form-label text-amber-800">
                                  Background Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    className="w-12 h-10 rounded border border-amber-300 cursor-pointer"
                                    value={tableStyle.rowBgColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        rowBgColor: e.target.value,
                                      }))
                                    }
                                  />
                                  <input
                                    type="text"
                                    className="form-input flex-1"
                                    value={tableStyle.rowBgColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        rowBgColor: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="form-label text-amber-800">Highlight Color</label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    className="w-12 h-10 rounded border border-amber-300 cursor-pointer"
                                    value={tableStyle.rowHoverColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        rowHoverColor: e.target.value,
                                      }))
                                    }
                                  />
                                  <input
                                    type="text"
                                    className="form-input flex-1"
                                    value={tableStyle.rowHoverColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        rowHoverColor: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="form-label text-amber-800">
                                  Highlight Text Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    className="w-12 h-10 rounded border border-amber-300 cursor-pointer"
                                    value={tableStyle.rowHoverTextColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        rowHoverTextColor: e.target.value,
                                      }))
                                    }
                                  />
                                  <input
                                    type="text"
                                    className="form-input flex-1"
                                    value={tableStyle.rowHoverTextColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        rowHoverTextColor: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="space-y-4">
                              <h4 className="font-medium text-amber-800">Column Header Colors</h4>
                              <div>
                                <label className="form-label text-amber-800">
                                  Header Background
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    className="w-12 h-10 rounded border border-amber-300 cursor-pointer"
                                    value={tableStyle.headerBgColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        headerBgColor: e.target.value,
                                      }))
                                    }
                                  />
                                  <input
                                    type="text"
                                    className="form-input flex-1"
                                    value={tableStyle.headerBgColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        headerBgColor: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="form-label text-amber-800">Text Color</label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    className="w-12 h-10 rounded border border-amber-300 cursor-pointer"
                                    value={tableStyle.rowTextColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        rowTextColor: e.target.value,
                                        headerTextColor: e.target.value,
                                      }))
                                    }
                                  />
                                  <input
                                    type="text"
                                    className="form-input flex-1"
                                    value={tableStyle.rowTextColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        rowTextColor: e.target.value,
                                        headerTextColor: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="border-t border-amber-200 pt-6">
                            <h4 className="font-medium text-amber-800 mb-4">Line Settings</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="form-label text-amber-800">Line Style</label>
                                <select
                                  className="form-input"
                                  value={tableStyle.borderStyle}
                                  onChange={(e) =>
                                    setTableStyle((prev) => ({
                                      ...prev,
                                      borderStyle: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="solid">Solid</option>
                                  <option value="dashed">Dashed</option>
                                  <option value="dotted">Dotted</option>
                                  <option value="none">None</option>
                                </select>
                              </div>
                              <div>
                                <label className="form-label text-amber-800">Line Thickness</label>
                                <select
                                  className="form-input"
                                  value={tableStyle.borderWidth}
                                  onChange={(e) =>
                                    setTableStyle((prev) => ({
                                      ...prev,
                                      borderWidth: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="0">None</option>
                                  <option value="1px">Thin (1px)</option>
                                  <option value="2px">Medium (2px)</option>
                                  <option value="3px">Thick (3px)</option>
                                  <option value="4px">Extra Thick (4px)</option>
                                </select>
                              </div>
                              <div>
                                <label className="form-label text-amber-800">Corner Rounding</label>
                                <select
                                  className="form-input"
                                  value={tableStyle.borderRadius}
                                  onChange={(e) =>
                                    setTableStyle((prev) => ({
                                      ...prev,
                                      borderRadius: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="0">None</option>
                                  <option value="0.25rem">Small (4px)</option>
                                  <option value="0.375rem">Default (6px)</option>
                                  <option value="0.5rem">Medium (8px)</option>
                                  <option value="0.75rem">Large (12px)</option>
                                  <option value="1rem">Extra Large (16px)</option>
                                </select>
                              </div>
                              <div>
                                <label className="form-label text-amber-800">Line Color</label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    className="w-12 h-10 rounded border border-amber-300 cursor-pointer"
                                    value={tableStyle.borderColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        borderColor: e.target.value,
                                        headerBorderColor: e.target.value,
                                        cellBorderColor: e.target.value,
                                      }))
                                    }
                                  />
                                  <input
                                    type="text"
                                    className="form-input flex-1"
                                    value={tableStyle.borderColor}
                                    onChange={(e) =>
                                      setTableStyle((prev) => ({
                                        ...prev,
                                        borderColor: e.target.value,
                                        headerBorderColor: e.target.value,
                                        cellBorderColor: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="mt-4 space-y-4">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  id="alternatingRows"
                                  checked={tableStyle.alternatingRowBg}
                                  onChange={(e) =>
                                    setTableStyle((prev) => ({
                                      ...prev,
                                      alternatingRowBg: e.target.checked,
                                    }))
                                  }
                                  className="w-4 h-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                                />
                                <label
                                  htmlFor="alternatingRows"
                                  className="form-label text-amber-800"
                                >
                                  Stripe Every Other Row
                                </label>
                              </div>

                              {tableStyle.alternatingRowBg && (
                                <div>
                                  <label className="form-label text-amber-800">Stripe Color</label>
                                  <div className="flex items-center space-x-3">
                                    <input
                                      type="color"
                                      className="w-12 h-10 rounded border border-amber-300 cursor-pointer"
                                      value={tableStyle.alternatingRowBgColor}
                                      onChange={(e) =>
                                        setTableStyle((prev) => ({
                                          ...prev,
                                          alternatingRowBgColor: e.target.value,
                                        }))
                                      }
                                    />
                                    <input
                                      type="text"
                                      className="form-input flex-1"
                                      value={tableStyle.alternatingRowBgColor}
                                      onChange={(e) =>
                                        setTableStyle((prev) => ({
                                          ...prev,
                                          alternatingRowBgColor: e.target.value,
                                        }))
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Columns Tab */}
              {activeTab === 'columns' && (
                <div className="space-y-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <p className="text-sm text-yellow-800">
                      💡 Check boxes to show/hide information columns. Use arrows to change order.
                      Changes save automatically.
                    </p>
                  </div>

                  <div className="space-y-2">
                    {columnConfig
                      .sort((a, b) => a.order - b.order)
                      .map((column, index) => (
                        <div
                          key={column.key}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <button className="text-gray-400 hover:text-gray-600 cursor-grab">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                              </svg>
                            </button>
                            <input
                              type="checkbox"
                              checked={column.visible}
                              onChange={(e) => {
                                setColumnConfig((prev) =>
                                  prev.map((col) =>
                                    col.key === column.key
                                      ? { ...col, visible: e.target.checked }
                                      : col
                                  )
                                );
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="font-medium text-gray-900">{column.label}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                              disabled={index === 0}
                              onClick={() => {
                                const newConfig = [...columnConfig];
                                const currentIndex = newConfig.findIndex(
                                  (c) => c.key === column.key
                                );
                                if (currentIndex > 0) {
                                  [newConfig[currentIndex], newConfig[currentIndex - 1]] = [
                                    newConfig[currentIndex - 1],
                                    newConfig[currentIndex],
                                  ];
                                  newConfig[currentIndex].order = currentIndex;
                                  newConfig[currentIndex - 1].order = currentIndex - 1;
                                  setColumnConfig(newConfig);
                                }
                              }}
                            >
                              Move Up
                            </button>
                            <button
                              className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                              disabled={index === columnConfig.length - 1}
                              onClick={() => {
                                const newConfig = [...columnConfig];
                                const currentIndex = newConfig.findIndex(
                                  (c) => c.key === column.key
                                );
                                if (currentIndex < newConfig.length - 1) {
                                  [newConfig[currentIndex], newConfig[currentIndex + 1]] = [
                                    newConfig[currentIndex + 1],
                                    newConfig[currentIndex],
                                  ];
                                  newConfig[currentIndex].order = currentIndex;
                                  newConfig[currentIndex + 1].order = currentIndex + 1;
                                  setColumnConfig(newConfig);
                                }
                              }}
                            >
                              Move Down
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <div className="mt-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                  onClick={() => setTableStylingOpen(false)}
                >
                  Done
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  onClick={() => {
                    // Reset to defaults
                    setTableStyle({
                      // Row styling
                      rowHeight: '3rem',
                      rowPadding: '0.75rem',
                      rowTextAlign: 'left',

                      // Typography
                      fontSize: '0.875rem',
                      fontFamily: 'system-ui',
                      fontWeight: '400',
                      lineHeight: '1.5',

                      // Colors
                      rowBgColor: '#ffffff',
                      rowTextColor: '#374151',
                      rowHoverColor: '#f9fafb',
                      rowHoverTextColor: '#374151',
                      alternatingRowBg: false,
                      alternatingRowBgColor: '#f8fafc',

                      // Borders
                      borderWidth: '1px',
                      borderColor: '#e5e7eb',
                      borderStyle: 'solid',
                      borderRadius: '0.375rem',

                      // Header styling
                      headerBgColor: '#f9fafb',
                      headerTextColor: '#374151',
                      headerFontWeight: '600',
                      headerFontSize: '0.875rem',
                      headerPadding: '0.75rem',
                      headerTextAlign: 'left',
                      headerBorderColor: '#e5e7eb',

                      // Cell styling
                      cellPadding: '0.75rem',
                      cellBorderColor: '#e5e7eb',
                      cellVerticalAlign: 'middle',
                    });
                    setColumnConfig([
                      { key: 'name', label: 'Name', visible: true, order: 0 },
                      { key: 'contact', label: 'Contact', visible: true, order: 1 },
                      { key: 'status', label: 'Status', visible: true, order: 2 },
                      {
                        key: 'interested_vehicle',
                        label: 'Interested Vehicle',
                        visible: true,
                        order: 3,
                      },
                      { key: 'deal_status', label: 'Deal Status', visible: true, order: 4 },
                      { key: 'salesperson', label: 'Salesperson', visible: true, order: 5 },
                      { key: 'created', label: 'Created', visible: true, order: 6 },
                      { key: 'actions', label: 'Actions', visible: true, order: 7 },
                    ]);
                  }}
                >
                  Reset to Defaults
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setDeleteConfirmOpen(false)}
            />

            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900">Delete Customer</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete {customerToDelete?.first_name}{' '}
                      {customerToDelete?.last_name}? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  onClick={confirmDelete}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  onClick={() => setDeleteConfirmOpen(false)}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
